import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Ship, Loader2, RefreshCw, Trash2, Plus, MapPin, Anchor, Truck, CheckCircle2, Clock, Lock, ChevronRight, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";

const STATUS = {
  IN_TRANSIT: { label: "In transit", cls: "bg-blue-50 text-blue-700", dot: "#0047AB" },
  DELIVERED: { label: "Delivered", cls: "bg-emerald-50 text-emerald-700", dot: "#1baf7a" },
  DELAYED: { label: "Delayed", cls: "bg-amber-50 text-amber-700", dot: "#eda100" },
  UNKNOWN: { label: "Unknown", cls: "bg-slate-100 text-slate-600", dot: "#94a3b8" },
};
const st = (s) => STATUS[s] || { label: (s || "—").replace(/_/g, " "), cls: "bg-slate-100 text-slate-600", dot: "#94a3b8" };
const fmt = (d) => {
  if (!d) return "—";
  const dt = new Date(String(d).replace(" ", "T"));
  return isNaN(dt.getTime()) ? String(d).slice(0, 16)
    : dt.toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

function QuotaBar({ quota, max }) {
  const uniq = quota?.unique_shipments;
  const calls = quota?.api_calls;
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid="tracking-quota">
      <div className="flex items-center gap-2 text-slate-700">
        <Ship className="w-4 h-4 text-primary" />
        <span className="font-display font-semibold text-sm">WE DELVR LOGISTICS Tracking</span>
      </div>
      <div className="text-xs font-num text-slate-500">
        Shipments <span className="font-semibold text-slate-800">{uniq ? uniq.used : "—"}/{uniq ? uniq.total : max}</span>
      </div>
      <div className="text-xs font-num text-slate-500">
        API calls <span className="font-semibold text-slate-800">{calls ? `${calls.used}/${calls.total}` : "—"}</span>
      </div>
      <div className="text-[11px] font-num text-slate-400 ml-auto">Subscription valid until 26 Jul 2026</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const s = st(status);
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${s.cls}`}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.dot }} /> {s.label}
    </span>
  );
}

function ShipmentCard({ item, onOpen, onRefresh, onDelete, readOnly, busyId }) {
  const s = item.summary || {};
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-all" data-testid={`tracking-card-${item.number}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-slate-900 text-sm">{item.number}</span>
            <StatusBadge status={s.status} />
            {s.sealine_name && <span className="text-[11px] text-slate-500 font-num">{s.sealine_name}</span>}
          </div>
          {item.label && <div className="text-xs text-slate-500 mt-0.5">{item.label}</div>}
          <div className="flex items-center gap-2 mt-2 text-sm text-slate-700 font-num">
            <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="truncate">{s.pol || "—"}</span>
            <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
            <span className="truncate">{s.pod || "—"}</span>
          </div>
          <div className="text-[11px] text-slate-400 mt-1 font-num">
            {s.eta ? `${s.eta_actual ? "Arrived" : "ETA"} ${fmt(s.eta)}` : "ETA —"} · {s.containers || 0} container{s.containers === 1 ? "" : "s"}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onOpen(item.id)} data-testid={`tracking-open-${item.number}`}
            className="text-xs text-primary hover:underline px-2 py-1">Details</button>
          {!readOnly && (
            <>
              <button onClick={() => onRefresh(item.id)} disabled={busyId === item.id} data-testid={`tracking-refresh-${item.number}`}
                className="text-slate-400 hover:text-primary p-1" title="Refresh from carrier">
                {busyId === item.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </button>
              <button onClick={() => onDelete(item.id)} data-testid={`tracking-delete-${item.number}`}
                className="text-slate-300 hover:text-destructive p-1" title="Remove"><Trash2 className="w-4 h-4" /></button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TrackMap({ data }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    const locs = Object.fromEntries((data.locations || []).map((l) => [l.id, l]));
    const route = data.route || {};
    const order = ["prepol", "pol", "pod", "postpod"];
    const stops = [];
    order.forEach((k) => {
      const node = route[k];
      const l = node && locs[node.location];
      if (l && l.lat != null && l.lng != null && !stops.find((s) => s.id === l.id)) {
        stops.push({ ...l, actual: node.actual });
      }
    });
    if (!stops.length) return undefined;

    const map = L.map(ref.current, { zoomControl: true, scrollWheelZoom: false, attributionControl: true });
    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      attribution: "&copy; OpenStreetMap &copy; CARTO", maxZoom: 18, subdomains: "abcd",
    }).addTo(map);

    const latlngs = stops.map((s) => [s.lat, s.lng]);
    L.polyline(latlngs, { color: "#0047AB", weight: 2.5, opacity: 0.65, dashArray: "6 7" }).addTo(map);
    stops.forEach((s, i) => {
      const role = i === 0 ? "Origin" : i === stops.length - 1 ? "Destination" : "Port";
      const color = i === stops.length - 1 && !s.actual ? "#eda100" : i === 0 ? "#1baf7a" : "#0047AB";
      L.circleMarker([s.lat, s.lng], {
        radius: i === 0 || i === stops.length - 1 ? 8 : 6,
        color: "#fff", weight: 2, fillColor: color, fillOpacity: 1,
      }).addTo(map).bindPopup(`<b>${s.name}, ${s.country_code}</b><br/><span style="color:#64748b">${role}</span>`);
    });
    map.fitBounds(latlngs, { padding: [42, 42], maxZoom: 6 });
    const t = setTimeout(() => map.invalidateSize(), 120);
    return () => { clearTimeout(t); map.remove(); };
  }, [data]);
  return <div ref={ref} className="h-60 w-full rounded-lg overflow-hidden border border-slate-200 z-0" data-testid="tracking-map" />;
}

function Timeline({ data }) {
  const locs = Object.fromEntries((data.locations || []).map((l) => [l.id, l]));
  const vessels = Object.fromEntries((data.vessels || []).map((v) => [v.id, v]));
  const containers = data.containers || [];
  return (
    <div className="space-y-6">
      {containers.map((c, ci) => (
        <div key={ci} data-testid={`tracking-container-${c.number}`}>
          <div className="flex items-center gap-2 mb-3">
            <span className="font-display font-bold text-slate-900 text-sm">{c.number}</span>
            {c.size_type && <span className="text-[11px] text-slate-500 font-num">{c.size_type}</span>}
            <StatusBadge status={c.status} />
          </div>
          <ol className="relative border-l-2 border-slate-100 ml-2">
            {(c.events || []).map((e, i) => {
              const l = locs[e.location];
              const v = vessels[e.vessel];
              const Icon = e.type === "sea" ? Anchor : Truck;
              return (
                <li key={i} className="ml-5 pb-4 last:pb-0" data-testid={`tracking-event-${ci}-${i}`}>
                  <span className={`absolute -left-[9px] flex items-center justify-center w-4 h-4 rounded-full ${e.actual ? "bg-primary" : "bg-white border-2 border-slate-300"}`}>
                    {e.actual && <CheckCircle2 className="w-3 h-3 text-white" />}
                  </span>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-800">{e.description}</span>
                    <span className={`text-[11px] font-num ${e.actual ? "text-slate-500" : "text-amber-600"}`}>
                      {e.actual ? fmt(e.date) : `${fmt(e.date)} · expected`}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-400 mt-0.5 font-num">
                    <Icon className="w-3 h-3" />
                    {l ? `${l.name}, ${l.country_code}` : "—"}
                    {v && ` · ${v.name}${e.voyage ? ` (${e.voyage})` : ""}`}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      ))}
      {!containers.length && <p className="text-sm text-slate-400">No container events available yet.</p>}
    </div>
  );
}

export function TrackingPanel({ readOnly = false }) {
  const [list, setList] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState({ number: "", type: "auto", sealine: "", label: "" });
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/sc/tracking"); setList(data); }
    catch (err) { toast.error(apiError(err?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.number.trim()) { toast.error("Enter a container / B/L / booking number."); return; }
    setAdding(true);
    try {
      await api.post("/sc/tracking", {
        number: form.number, type: form.type === "auto" ? null : form.type,
        sealine: form.sealine || null, label: form.label || null,
      }, { timeout: 95000 });
      toast.success("Shipment tracked.");
      setForm({ number: "", type: "auto", sealine: "", label: "" });
      load();
    } catch (err) { toast.error(apiError(err?.response?.data?.detail, "Could not track that shipment.")); }
    finally { setAdding(false); }
  };

  const refresh = async (id) => {
    setBusyId(id);
    try { await api.post(`/sc/tracking/${id}/refresh`, {}, { timeout: 95000 }); toast.success("Updated from carrier."); load(); }
    catch (err) { toast.error(apiError(err?.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const del = async (id) => {
    try { await api.delete(`/sc/tracking/${id}`); load(); if (detail?.id === id) setDetail(null); }
    catch (err) { toast.error(apiError(err?.response?.data?.detail)); }
  };

  const open = async (id) => {
    setLoadingDetail(true);
    try { const { data } = await api.get(`/sc/tracking/${id}`); setDetail(data); }
    catch (err) { toast.error(apiError(err?.response?.data?.detail)); }
    finally { setLoadingDetail(false); }
  };

  const items = list?.items || [];

  return (
    <div className="space-y-6" data-testid="tracking-panel">
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Container tracking</h2>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          Live shipment tracking by container, Bill of Lading or booking number, powered by WDL (WE DELVR LOGISTICS) Tracking. Status is cached — viewing
          a shipment does not use your subscription; only adding a new shipment or refreshing pulls fresh data from the carrier.
        </p>
      </div>

      <QuotaBar quota={list?.quota} max={list?.max_shipments} />

      {readOnly && (
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-600" data-testid="tracking-readonly-banner">
          <Lock className="w-4 h-4 text-slate-400" />
          View-only access — shipment statuses are shared read-only. Adding & refreshing shipments is restricted to admins.
        </div>
      )}

      {!readOnly && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="tracking-add-form">
          <h3 className="font-display font-semibold text-sm text-slate-900 mb-3">Track a new shipment</h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })}
              placeholder="Container / B/L / Booking no." data-testid="tracking-number-input"
              className="rounded-md border border-slate-200 px-3 py-2 text-sm font-num focus:outline-none focus:ring-2 focus:ring-primary/30" />
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger className="h-[38px]" data-testid="tracking-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto-detect type</SelectItem>
                <SelectItem value="CT">Container (CT)</SelectItem>
                <SelectItem value="BL">Bill of Lading (BL)</SelectItem>
                <SelectItem value="BK">Booking (BK)</SelectItem>
              </SelectContent>
            </Select>
            <input value={form.sealine} onChange={(e) => setForm({ ...form, sealine: e.target.value })}
              placeholder="Carrier SCAC (optional)" data-testid="tracking-sealine-input"
              className="rounded-md border border-slate-200 px-3 py-2 text-sm font-num focus:outline-none focus:ring-2 focus:ring-primary/30" />
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Label / reference (optional)" data-testid="tracking-label-input"
              className="rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <button onClick={add} disabled={adding} data-testid="tracking-add-btn"
            className="mt-3 inline-flex items-center gap-2 rounded-lg bg-primary text-white px-4 py-2.5 text-sm font-medium hover:bg-primary/90 disabled:opacity-60">
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {adding ? "Contacting carrier…" : "Track shipment"}
          </button>
        </div>
      )}

      {!list ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : items.length ? (
        <div className="grid gap-3">
          {items.map((it) => (
            <ShipmentCard key={it.id} item={it} readOnly={readOnly} busyId={busyId}
              onOpen={open} onRefresh={refresh} onDelete={del} />
          ))}
        </div>
      ) : (
        <div className="text-center text-sm text-slate-400 py-10 border border-dashed border-slate-200 rounded-xl" data-testid="tracking-empty">
          No shipments tracked yet.{!readOnly && " Add a container, B/L or booking number above."}
        </div>
      )}

      {(detail || loadingDetail) && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto" data-testid="tracking-detail-modal"
          onClick={() => setDetail(null)}>
          <div className="bg-white rounded-xl w-full max-w-2xl my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-xl">
              <Ship className="w-5 h-5 text-primary" />
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-slate-900">{detail?.number || "Loading…"}</div>
                {detail && <div className="text-[11px] text-slate-500 font-num">{detail.summary?.sealine_name} · updated {fmt(detail.summary?.provider_updated_at)}</div>}
              </div>
              {detail && <StatusBadge status={detail.summary?.status} />}
              <button onClick={() => setDetail(null)} data-testid="tracking-detail-close" className="text-slate-400 hover:text-slate-700 p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              {loadingDetail || !detail ? (
                <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
              ) : (
                <>
                  <TrackMap data={detail.raw || {}} />
                  <div className="grid grid-cols-3 gap-3 mt-4 mb-6">
                    <div className="rounded-lg bg-slate-50 p-3"><div className="text-[10px] uppercase tracking-wider text-slate-400 font-num">Origin</div><div className="text-sm font-medium text-slate-800 mt-1">{detail.summary?.pol || "—"}</div></div>
                    <div className="rounded-lg bg-slate-50 p-3"><div className="text-[10px] uppercase tracking-wider text-slate-400 font-num">Destination</div><div className="text-sm font-medium text-slate-800 mt-1">{detail.summary?.pod || "—"}</div></div>
                    <div className="rounded-lg bg-slate-50 p-3"><div className="text-[10px] uppercase tracking-wider text-slate-400 font-num flex items-center gap-1"><Clock className="w-3 h-3" />{detail.summary?.eta_actual ? "Arrived" : "ETA"}</div><div className="text-sm font-medium text-slate-800 mt-1">{fmt(detail.summary?.eta)}</div></div>
                  </div>
                  <Timeline data={detail.raw || {}} />
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
